export type StudentHistoryImportStatus = 'ready' | 'ambiguous' | 'duplicate' | 'blocked';

export interface StudentHistoryCandidate {
  id: number;
  fullName: string;
}

export interface StudentHistoryImportRow {
  id: string;
  studentName: string;
  code: string;
  currentTrainingCode: string | null;
  periodYear: number;
  periodMonth: number;
  workoutDay: number | null;
  expectedDeliveryDay: number | null;
  status: StudentHistoryImportStatus;
  reason: string | null;
  studentId: number | null;
  matchedStudentName: string | null;
  candidateStudents: StudentHistoryCandidate[];
}

export interface StudentDirectoryEntry {
  id: number;
  fullName: string;
  searchName: string;
}

export type StudentNameMatch =
  | { status: 'one'; studentId: number; fullName: string }
  | { status: 'none' }
  | { status: 'many'; candidates: StudentHistoryCandidate[] };

export type TrainingCodeMatch =
  | { status: 'one'; trainingId: number }
  | { status: 'none' }
  | { status: 'many' };

export interface StudentHistoryMatchResult {
  studentsByName: Record<string, StudentNameMatch>;
  trainingsByCode: Record<string, TrainingCodeMatch>;
  existingCompetences: readonly { studentId: number; previousCompetence: string }[];
}

const WORKOUT_HISTORY_DOMAIN = 'WorkoutHistory';
const REASON_NO_PREVIOUS = 'Sem código anterior';
const REASON_TWO_CODES = 'Dois códigos no mesmo mês';
const REASON_STUDENT = 'Aluna não encontrada';
const REASON_STUDENTS = 'Mais de uma aluna com nome compatível';
const REASON_TRAINING = 'Treino não cadastrado';
const REASON_TRAININGS = 'Mais de um treino com esse código';
const REASON_DUPLICATE = 'Já cadastrado';

const MONTH_LABELS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

const statusOrder: Record<StudentHistoryImportStatus, number> = {
  ready: 0,
  ambiguous: 1,
  duplicate: 2,
  blocked: 3,
};

interface MonthCell {
  year: number;
  month: number;
  codes: string[];
  keep: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const readText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const readMonthNumber = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : Number.NaN;
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > 12) {
    return null;
  }
  return parsed;
};

const readYearNumber = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : Number.NaN;
  if (!Number.isInteger(parsed) || parsed < 1900 || parsed > 2100) {
    return null;
  }
  return parsed;
};

export const normalizeHistoryCode = (code: string): string => {
  const trimmed = code.trim();
  const stripped = trimmed.replace(/-?novo$/i, '');
  const value = stripped.length > 0 ? stripped : trimmed;
  return value.toUpperCase();
};

export const normalizeHistorySearchText = (value: string): string => (
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\u00e7/gi, 'c')
    .replace(/\u00f1/gi, 'n')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
);

const SOURCE_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const historyCompetenceIso = (year: number, month: number): string => (
  `${year}-${String(month).padStart(2, '0')}-01`
);

export const readHistorySourceDay = (value: unknown): number | null => {
  if (typeof value !== 'string' || !SOURCE_DATE.test(value.trim())) {
    return null;
  }

  const [year, month, day] = value.trim().split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year
    || parsed.getUTCMonth() !== month - 1
    || parsed.getUTCDate() !== day
  ) {
    return null;
  }

  return day;
};

export const historyMonthDate = (year: number, month: number, day: number | null): string => {
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const clamped = Math.min(day ?? 1, lastDay);
  return `${year}-${String(month).padStart(2, '0')}-${String(clamped).padStart(2, '0')}`;
};

export const historyCompetenceKey = (studentId: number, year: number, month: number): string => (
  `${studentId}|${historyCompetenceIso(year, month)}`
);

export const formatHistoryMonth = (year: number, month: number): string => {
  const label = MONTH_LABELS[month - 1];
  return label ? `${label} ${year}` : `${month}/${year}`;
};

const monthKey = (year: number, month: number): string => `${year}-${month}`;

const compareMonths = (
  left: { periodYear: number; periodMonth: number },
  right: { periodYear: number; periodMonth: number },
): number => (
  left.periodYear - right.periodYear || left.periodMonth - right.periodMonth
);

export const sortStudentHistoryRows = (
  rows: readonly StudentHistoryImportRow[],
): StudentHistoryImportRow[] => (
  [...rows].sort((left, right) => (
    statusOrder[left.status] - statusOrder[right.status]
    || left.studentName.localeCompare(right.studentName, 'pt-BR')
    || compareMonths(left, right)
    || left.code.localeCompare(right.code, 'pt-BR')
  ))
);

const rowId = (
  sourceRowNumber: number,
  year: number,
  month: number,
  code: string,
): string => `${sourceRowNumber}:${year}-${month}:${code}`;

const buildRow = (
  sourceRowNumber: number,
  studentName: string,
  year: number,
  month: number,
  code: string,
  currentTrainingCode: string | null,
  workoutDay: number | null,
  expectedDeliveryDay: number | null,
  status: StudentHistoryImportStatus,
  reason: string | null,
): StudentHistoryImportRow => ({
  id: rowId(sourceRowNumber, year, month, code),
  studentName,
  code,
  currentTrainingCode,
  periodYear: year,
  periodMonth: month,
  workoutDay,
  expectedDeliveryDay,
  status,
  reason,
  studentId: null,
  matchedStudentName: null,
  candidateStudents: [],
});

const resolveStudentMonths = (
  sourceRowNumber: number,
  studentName: string,
  records: readonly unknown[],
  workoutDay: number | null,
  expectedDeliveryDay: number | null,
): StudentHistoryImportRow[] => {
  const months = new Map<string, MonthCell>();

  records.forEach((record) => {
    if (!isRecord(record)) {
      return;
    }

    const year = readYearNumber(record.periodYear);
    const month = readMonthNumber(record.periodMonth);
    if (year === null || month === null) {
      return;
    }

    const key = monthKey(year, month);
    const cell = months.get(key) ?? { year, month, codes: [], keep: false };
    if (record.entryKind === 'Prescription') {
      const code = normalizeHistoryCode(readText(record.parsedCode));
      if (code.length > 0) {
        cell.codes.push(code);
      }
    } else if (record.entryKind === 'KeepPrevious') {
      cell.keep = true;
    }
    months.set(key, cell);
  });

  const ordered = Array.from(months.values()).sort((left, right) => left.year - right.year || left.month - right.month);
  const rows: StudentHistoryImportRow[] = [];
  let lastCode: string | null = null;

  ordered.forEach((cell) => {
    const uniqueCodes = Array.from(new Set<string>(cell.codes));
    if (uniqueCodes.length > 1) {
      uniqueCodes.forEach((code) => {
        rows.push(buildRow(
          sourceRowNumber,
          studentName,
          cell.year,
          cell.month,
          code,
          lastCode,
          workoutDay,
          expectedDeliveryDay,
          'blocked',
          REASON_TWO_CODES,
        ));
      });
      return;
    }

    if (uniqueCodes.length === 1) {
      const code = uniqueCodes[0];
      rows.push(buildRow(
        sourceRowNumber,
        studentName,
        cell.year,
        cell.month,
        code,
        lastCode,
        workoutDay,
        expectedDeliveryDay,
        'ready',
        null,
      ));
      lastCode = code;
      return;
    }

    if (!cell.keep) {
      return;
    }

    if (lastCode === null) {
      rows.push(buildRow(
        sourceRowNumber,
        studentName,
        cell.year,
        cell.month,
        '',
        null,
        workoutDay,
        expectedDeliveryDay,
        'blocked',
        REASON_NO_PREVIOUS,
      ));
      return;
    }

    rows.push(buildRow(
      sourceRowNumber,
      studentName,
      cell.year,
      cell.month,
      lastCode,
      lastCode,
      workoutDay,
      expectedDeliveryDay,
      'ready',
      null,
    ));
  });

  return rows;
};

export const parseStudentWorkoutHistoryImport = (payload: unknown): StudentHistoryImportRow[] => {
  if (!isRecord(payload) || !isRecord(payload.metadata) || payload.metadata.domain !== WORKOUT_HISTORY_DOMAIN) {
    throw new Error('Envie um JSON de histórico de treinos (workout-history).');
  }

  if (!Array.isArray(payload.students)) {
    throw new Error('Envie um JSON de histórico de treinos (workout-history).');
  }

  const rows: StudentHistoryImportRow[] = [];

  payload.students.forEach((student, index) => {
    if (!isRecord(student) || !Array.isArray(student.records)) {
      return;
    }

    const studentName = readText(student.studentName);
    if (studentName.length === 0) {
      return;
    }

    const sourceRowNumber = typeof student.sourceRowNumber === 'number' && Number.isInteger(student.sourceRowNumber)
      ? student.sourceRowNumber
      : index + 1;
    rows.push(...resolveStudentMonths(
      sourceRowNumber,
      studentName,
      student.records,
      readHistorySourceDay(student.workoutCompletedOn),
      readHistorySourceDay(student.expectedDeliveryOn),
    ));
  });

  return sortStudentHistoryRows(rows);
};

const toCandidate = (student: StudentDirectoryEntry): StudentHistoryCandidate => ({
  id: student.id,
  fullName: student.fullName,
});

const LEADING_SHEET_INDEX = /^\d+\s*-\s*/;
const ALIAS_SEPARATOR = /\s+(?:c\/c\/|cc\/|c\/c|cc):?\s*/;
const PARENTHESES = /\([^)]*\)/g;
const LEADING_ALIAS = /^(?:c\/c\/|cc\/|c\/c|cc):?\s+/;
const MIN_PREFIX_TOKENS = 3;
const MAX_EXTRA_PREFIX_TOKENS = 2;

const tidyName = (value: string): string => value.replace(/["']/g, ' ').replace(/\s+/g, ' ').trim();

const tokensOf = (value: string): string[] => value.split(' ').filter((token) => token.length > 0);

const withoutParentheses = (value: string): string => tidyName(value.replace(PARENTHESES, ' '));

const innerNames = (value: string): string[] => {
  const names: string[] = [];
  const pattern = /\(([^)]*)\)/g;
  let found = pattern.exec(value);
  while (found) {
    const inner = tidyName(found[1]).replace(LEADING_ALIAS, '');
    if (tokensOf(inner).length >= MIN_PREFIX_TOKENS) {
      names.push(inner);
    }
    found = pattern.exec(value);
  }
  return names;
};

const spreadsheetNameQueries = (studentName: string): string[] => {
  const normalized = tidyName(normalizeHistorySearchText(studentName).replace(LEADING_SHEET_INDEX, ''));
  if (normalized.length === 0) {
    return [];
  }

  const segments = normalized.split(ALIAS_SEPARATOR).map(tidyName).filter((part) => part.length > 0);
  const queries: string[] = [];
  const push = (value: string) => {
    const clean = tidyName(value);
    if (clean.length > 0 && !queries.includes(clean)) {
      queries.push(clean);
    }
  };

  (segments.length > 0 ? segments : [normalized]).forEach((segment) => {
    push(withoutParentheses(segment));
    innerNames(segment).forEach(push);
  });

  return queries;
};

const matchQuery = (
  query: string,
  students: readonly StudentDirectoryEntry[],
  allowPrefix: boolean,
): StudentNameMatch => {
  const queryTokens = tokensOf(query);
  if (queryTokens.length === 0) {
    return { status: 'none' };
  }

  const directory = students.map((student) => ({
    student,
    searchName: normalizeHistorySearchText(student.searchName || student.fullName),
  }));

  const exact = directory.filter((item) => item.searchName === query);
  if (exact.length === 1) {
    return { status: 'one', studentId: exact[0].student.id, fullName: exact[0].student.fullName };
  }
  if (exact.length > 1) {
    return { status: 'many', candidates: exact.map((item) => toCandidate(item.student)) };
  }

  const tokenHits = directory.filter((item) => {
    const tokens = new Set(tokensOf(item.searchName));
    return queryTokens.every((token) => tokens.has(token));
  });
  if (tokenHits.length === 1) {
    return { status: 'one', studentId: tokenHits[0].student.id, fullName: tokenHits[0].student.fullName };
  }
  if (tokenHits.length > 1) {
    return { status: 'many', candidates: tokenHits.map((item) => toCandidate(item.student)) };
  }

  if (!allowPrefix) {
    return { status: 'none' };
  }

  const prefixHits = directory.filter((item) => {
    const tokens = tokensOf(item.searchName);
    const extra = queryTokens.length - tokens.length;
    if (tokens.length < MIN_PREFIX_TOKENS || extra < 1 || extra > MAX_EXTRA_PREFIX_TOKENS) {
      return false;
    }
    return tokens.every((token, index) => queryTokens[index] === token);
  });
  if (prefixHits.length === 0) {
    return { status: 'none' };
  }

  const longest = Math.max(...prefixHits.map((item) => tokensOf(item.searchName).length));
  const best = prefixHits.filter((item) => tokensOf(item.searchName).length === longest);
  if (best.length === 1) {
    return { status: 'one', studentId: best[0].student.id, fullName: best[0].student.fullName };
  }

  return { status: 'many', candidates: best.map((item) => toCandidate(item.student)) };
};

export const matchStudentName = (
  studentName: string,
  students: readonly StudentDirectoryEntry[],
): StudentNameMatch => {
  const queries = spreadsheetNameQueries(studentName);
  if (queries.length === 0) {
    return { status: 'none' };
  }

  for (const query of queries) {
    const match = matchQuery(query, students, false);
    if (match.status !== 'none') {
      return match;
    }
  }

  return matchQuery(queries[0], students, true);
};

const existingKeySet = (
  existingCompetences: readonly { studentId: number; previousCompetence: string }[],
): Set<string> => new Set(
  existingCompetences.map((item) => `${item.studentId}|${item.previousCompetence.slice(0, 10)}`),
);

const withStudentChoice = (
  row: StudentHistoryImportRow,
  studentId: number,
  fullName: string,
  existing: Set<string>,
): StudentHistoryImportRow => {
  const duplicate = existing.has(historyCompetenceKey(studentId, row.periodYear, row.periodMonth));
  return {
    ...row,
    status: duplicate ? 'duplicate' : 'ready',
    reason: duplicate ? REASON_DUPLICATE : null,
    studentId,
    matchedStudentName: fullName,
    candidateStudents: [],
  };
};

export const applyStudentHistoryMatches = (
  rows: readonly StudentHistoryImportRow[],
  result: StudentHistoryMatchResult,
): StudentHistoryImportRow[] => {
  const existing = existingKeySet(result.existingCompetences);

  return sortStudentHistoryRows(rows.map((row) => {
    if (row.status === 'blocked') {
      return row;
    }

    const student = result.studentsByName[row.studentName] ?? { status: 'none' as const };
    if (student.status === 'none') {
      return {
        ...row,
        status: 'blocked',
        reason: REASON_STUDENT,
      };
    }

    const training = result.trainingsByCode[row.code];
    if (!training || training.status === 'none') {
      return {
        ...row,
        status: 'blocked',
        reason: REASON_TRAINING,
      };
    }
    if (training.status === 'many') {
      return {
        ...row,
        status: 'blocked',
        reason: REASON_TRAININGS,
      };
    }

    if (student.status === 'many') {
      return {
        ...row,
        status: 'ambiguous',
        reason: REASON_STUDENTS,
        candidateStudents: student.candidates,
      };
    }

    return withStudentChoice(row, student.studentId, student.fullName, existing);
  }));
};

export const chooseStudentHistoryMatch = (
  rows: readonly StudentHistoryImportRow[],
  studentName: string,
  studentId: number | null,
  existingCompetences: readonly { studentId: number; previousCompetence: string }[],
): StudentHistoryImportRow[] => {
  const existing = existingKeySet(existingCompetences);

  return sortStudentHistoryRows(rows.map((row) => {
    if (row.studentName !== studentName || row.status === 'blocked' || row.candidateStudents.length === 0) {
      return row;
    }

    if (studentId === null) {
      return {
        ...row,
        status: 'ambiguous',
        reason: REASON_STUDENTS,
        studentId: null,
        matchedStudentName: null,
      };
    }

    const chosen = row.candidateStudents.find((candidate) => candidate.id === studentId);
    if (!chosen) {
      return row;
    }

    return {
      ...withStudentChoice(row, chosen.id, chosen.fullName, existing),
      candidateStudents: row.candidateStudents,
    };
  }));
};
